import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, ClipboardList, User, Calendar, Armchair, Users, Receipt, CheckCircle, Clock, ChefHat, Wine } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const fmt = (n) => `$${(n || 0).toFixed(2)}`;

function isLight(hex = '') {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return false;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160;
}
function darkenHex(hex = '', amount = 55) {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return hex;
  return '#' + [0, 2, 4].map(i =>
    Math.max(0, parseInt(h.substring(i, i + 2), 16) - amount).toString(16).padStart(2, '0')
  ).join('');
}

const STATUS_CONFIG = {
  'Order Placed':           { color: '#3b82f6', bg: '#eff6ff', label: 'Placed' },
  'Order Accepted':         { color: '#6366f1', bg: '#eef2ff', label: 'Accepted' },
  'In Preparation':         { color: '#f59e0b', bg: '#fffbeb', label: 'Preparing' },
  'Ready to Pick':          { color: '#8b5cf6', bg: '#f5f3ff', label: 'Ready' },
  'Picked Up':              { color: '#06b6d4', bg: '#ecfeff', label: 'Picked Up' },
  'Delivered':              { color: '#10b981', bg: '#ecfdf5', label: 'Delivered' },
  'Cancelled by Customer':  { color: '#ef4444', bg: '#fef2f2', label: 'Cancelled' },
  'Cancelled By Staff':     { color: '#ef4444', bg: '#fef2f2', label: 'Cancelled' },
};

export const CustomerOrders = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [bill, setBill] = useState(null);
  const [seats, setSeats] = useState([]);
  const [staff, setStaff] = useState([]);

  useEffect(() => { fetchAll(); }, [deviceId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const deviceRes = await axios.get(`${BACKEND_URL}/api/device/${deviceId}/data`);
      if (deviceRes.data.code !== 200) { setError(deviceRes.data.message || 'No allocation found'); return; }
      const dd = deviceRes.data.data;
      setData(dd);
      const allocationId = dd.allocation?.id;
      const propertyId = dd.allocation?.propertyId || dd.property?.id;
      if (!allocationId) { setError('No active allocation'); return; }
      const reqs = [
        axios.get(`${BACKEND_URL}/api/allocations/${allocationId}/orders`),
        axios.get(`${BACKEND_URL}/api/billing/${allocationId}`),
        propertyId && axios.get(`${BACKEND_URL}/api/seats/${propertyId}`),
        propertyId && axios.get(`${BACKEND_URL}/api/staff/${propertyId}`),
      ].filter(Boolean);
      const results = await Promise.all(reqs);
      if (results[0]?.data?.code === 200) setOrders(results[0].data.data || []);
      if (results[1]?.data?.code === 200) setBill(results[1].data.data);
      if (results[2]?.data?.success) setSeats(results[2].data.seats || []);
      if (results[3]?.data?.success) setStaff(results[3].data.staff || []);
    } catch { setError('Failed to load orders.'); }
    finally { setLoading(false); }
  };

  const allocation = data?.allocation;
  const themeColor = data?.property?.themeColor || '#92400e';
  const darkColor = darkenHex(themeColor, 55);
  const textOnTheme = isLight(themeColor) ? '#1c1917' : '#ffffff';
  const mutedOnTheme = isLight(themeColor) ? 'rgba(28,25,23,0.65)' : 'rgba(255,255,255,0.65)';
  const logoUrl = data?.property?.logoUrl || '';

  const getSeatNumbers = () => {
    if (!allocation?.seatIds?.length || !seats.length) return '—';
    return allocation.seatIds.map(id => seats.find(x => x.id === id)?.seatNumber || id).join(', ');
  };
  const getStaffName = (id) => staff.find(x => x.id === id)?.name || '—';
  const getStaffNames = (ids) => (ids || []).map(id => staff.find(x => x.id === id)?.name).filter(Boolean);

  const getBillTotal = () => {
    if (bill?.grandTotal !== undefined) return bill.grandTotal;
    return orders.filter(o => !['Cancelled by Customer', 'Cancelled By Staff'].includes(o.status))
      .reduce((s, o) => s + (o.totalAmount || 0), 0);
  };

  const activeOrders = orders.filter(o => !['Cancelled by Customer', 'Cancelled By Staff'].includes(o.status));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"
      style={{ background: `linear-gradient(160deg, ${themeColor} 0%, ${darkenHex(themeColor, 55)} 100%)` }}>
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        <p className="text-white/70 text-sm font-medium">Loading orders…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-rose-500" />
        </div>
        <h2 className="text-xl font-bold text-stone-800 mb-2">Oops!</h2>
        <p className="text-stone-500 text-sm mb-4">{error}</p>
        <button onClick={() => navigate(`/customer/menu/${deviceId}`)} className="font-medium text-sm" style={{ color: themeColor }}>
          Back to Menu
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-100 pb-10">

      {/* ── Full-width logo strip ── */}
      <div className="w-full bg-white shadow-sm flex items-center justify-center px-6 py-3" style={{ minHeight: '56px' }}>
        {logoUrl
          ? <img src={logoUrl} alt="logo" className="h-10 w-auto max-w-[180px] object-contain" data-testid="property-logo" />
          : <span className="text-base font-bold text-stone-700">{data?.property?.name || 'SmartFlags'}</span>}
      </div>

      {/* ── Theme-coloured header ── */}
      <div className="relative overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${themeColor} 0%, ${darkColor} 100%)` }}>
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="relative z-10 max-w-2xl mx-auto px-5 pt-5 pb-5 flex items-center gap-4">
          <button onClick={() => navigate(`/customer/menu/${deviceId}`)}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-90 shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            data-testid="orders-back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: textOnTheme }} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: textOnTheme }}>My Orders</h1>
            {allocation && (
              <p className="text-sm" style={{ color: mutedOnTheme }}>
                {allocation.guestName} · Room {allocation.roomNumber}
              </p>
            )}
          </div>
          {activeOrders.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.15)' }}>
              <ClipboardList className="w-4 h-4" style={{ color: textOnTheme }} />
              <span className="text-sm font-bold" style={{ color: textOnTheme }}>{activeOrders.length}</span>
            </div>
          )}
        </div>
        <svg viewBox="0 0 1440 20" className="w-full -mb-px" preserveAspectRatio="none" style={{ height: '20px', fill: '#e7e5e4' }}>
          <path d="M0,10 C360,20 1080,0 1440,10 L1440,20 L0,20 Z" />
        </svg>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-3">

        {/* ── Allocation details ── */}
        {allocation && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="customer-orders-allocation-details">
            <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
              <div className="w-1 h-4 rounded-full" style={{ backgroundColor: themeColor }} />
              <span className="text-sm font-bold text-stone-700">Allocation Details</span>
            </div>
            <div className="grid grid-cols-2 gap-0 divide-x divide-y divide-stone-100">
              {[
                { icon: User, label: 'Guest', value: allocation.guestName, color: themeColor },
                { icon: Calendar, label: 'Room', value: allocation.roomNumber, color: themeColor },
                { icon: Armchair, label: 'Seats', value: getSeatNumbers(), color: themeColor },
                { icon: Users, label: 'Adults / Kids', value: `${allocation.numAdults || 0} / ${allocation.numKids || 0}`, color: themeColor },
              ].map(({ icon: Icon, label, value, color }, i) => (
                <div key={i} className="p-4 flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color }} />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">{label}</p>
                    <p className="text-sm font-semibold text-stone-800 mt-0.5">{value}</p>
                  </div>
                </div>
              ))}
              {allocation.fbManagerId && (
                <div className="p-4 flex items-start gap-2.5 col-span-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: `${themeColor}15` }}>
                    <User className="w-3.5 h-3.5" style={{ color: themeColor }} />
                  </div>
                  <div>
                    <p className="text-[10px] text-stone-400 uppercase tracking-wide font-semibold">F&B Manager</p>
                    <p className="text-sm font-semibold text-stone-800 mt-0.5">{getStaffName(allocation.fbManagerId)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Orders ── */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: `${themeColor}12` }}>
              <ClipboardList className="w-7 h-7" style={{ color: themeColor }} />
            </div>
            <h2 className="text-base font-bold text-stone-700 mb-1">No orders yet</h2>
            <p className="text-stone-400 text-sm">Place an order from the menu to see it here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="customer-orders-list">
            {orders.map((order) => {
              const sc = STATUS_CONFIG[order.status] || { color: '#64748b', bg: '#f1f5f9', label: order.status };
              const isKitchen = order.orderType !== 'Bar';
              return (
                <div key={order.id} className="bg-white rounded-2xl shadow-sm overflow-hidden"
                  data-testid={`customer-order-${order.id}`}>
                  {/* Order header */}
                  <div className="px-4 py-3 flex items-center justify-between gap-2 border-b border-stone-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${themeColor}15` }}>
                        {isKitchen
                          ? <ChefHat className="w-3.5 h-3.5" style={{ color: themeColor }} />
                          : <Wine className="w-3.5 h-3.5" style={{ color: themeColor }} />}
                      </div>
                      <span className="text-xs font-mono text-stone-400 truncate max-w-[100px]">#{order.id?.slice(-8)}</span>
                    </div>
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0"
                      style={{ backgroundColor: sc.bg, color: sc.color }}>
                      {sc.label}
                    </span>
                  </div>

                  {/* Items */}
                  <div className="px-4 py-3 space-y-2">
                    {order.items?.map((item, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between text-sm items-baseline gap-2">
                          <span className="text-stone-700 flex-1 line-clamp-1">
                            {item.menuItemName}
                            <span className="text-stone-400 ml-1">×{item.quantity}</span>
                          </span>
                          <span className="font-semibold text-stone-800 shrink-0">{fmt(item.price * item.quantity)}</span>
                        </div>
                        {item.notes && (
                          <p className="text-xs italic mt-0.5 px-2 py-0.5 rounded"
                            style={{ backgroundColor: `${themeColor}12`, color: themeColor }}>
                            {item.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-2.5 flex items-center justify-between border-t border-stone-100 bg-stone-50/60">
                    <span className="flex items-center gap-1 text-xs text-stone-400">
                      <Clock className="w-3 h-3" />
                      {new Date(order.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="text-sm font-bold" style={{ color: themeColor }}>{fmt(order.totalAmount)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Bill Summary ── */}
        {orders.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="customer-orders-bill-summary">
            <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
              <div className="w-1 h-4 rounded-full" style={{ backgroundColor: themeColor }} />
              <Receipt className="w-4 h-4 text-stone-400" />
              <span className="text-sm font-bold text-stone-700">Bill Summary</span>
            </div>
            <div className="p-5 space-y-2.5 text-sm">
              {(() => {
                const itemMap = {};
                activeOrders.forEach(o => {
                  (o.items || []).forEach(it => {
                    if (itemMap[it.menuItemId]) itemMap[it.menuItemId].quantity += it.quantity;
                    else itemMap[it.menuItemId] = { name: it.menuItemName, quantity: it.quantity, price: it.price };
                  });
                });
                return Object.values(itemMap).map((it, idx) => (
                  <div key={idx} className="flex justify-between text-stone-600">
                    <span className="flex-1 line-clamp-1">{it.name} <span className="text-stone-400">×{it.quantity}</span></span>
                    <span className="shrink-0 ml-2">{fmt(it.price * it.quantity)}</span>
                  </div>
                ));
              })()}
              {bill?.overallDiscountAmount > 0 && (
                <div className="flex justify-between text-rose-500 pt-2 border-t border-stone-100">
                  <span>Discount</span><span>-{fmt(bill.overallDiscountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t-2 border-stone-200">
                <span className="font-bold text-stone-800 text-base">Total</span>
                <span className="font-extrabold text-xl" style={{ color: themeColor }} data-testid="customer-bill-total">
                  {fmt(getBillTotal())}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Back to menu */}
        <button onClick={() => navigate(`/customer/menu/${deviceId}`)}
          className="w-full py-3.5 rounded-2xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
          style={{ backgroundColor: themeColor }}>
          <ArrowLeft className="w-4 h-4" /> Back to Menu
        </button>
      </div>
    </div>
  );
};
